import React, { useCallback, useEffect, useRef, useState } from "react"
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min"
import {
  exportProcedure,
  getProcedure,
  removeProcedure,
  reorderProcedureStage,
  updateProcedure,
  updateProcedureStage,
  updateStageTask,
} from "~/services/apis"
import { useProcedure, useProcedureStage, useStageTask } from "~/hooks"
import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  Drawer,
  message,
  Modal,
  Popconfirm,
  Progress,
  Tag,
} from "antd"
import usePageConfigContext from "~/hooks/usePageConfigContext"
import {
  HolderOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
  CommentOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import AddStageModal from "./AddStageModal"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import AddTaskModal from "./AddTaskModal"
import { UserTag } from "~/components"
import { debounce } from "lodash"
import AssignToModal from "./AssignToModal"
import { getAvatarBackgroundColor } from "~/utils"
import { TASK_STATUS_COLOR } from "~/constants"
import CommentList from "./CommentList"
import useDiary from "~/hooks/useDiary"

function ProcedurePage() {
  const { id } = useParams()
  const history = useHistory()
  const [procedure, setProcedure] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [updatingProcedureStage, setUpdatingProcedureStage] = useState(false)
  const [addStageModalOpen, setAddStageModalOpen] = useState(false)
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false)
  const [assignedToModalOpen, setAssignedToModalOpen] = useState(false)
  const [commentListOpen, setCommentListOpen] = useState(false)
  const [commentModalData, setCommentModalData] = useState(null)
  const [selectedStageTasks, setSelectedStageTasks] = useState([])
  const { setPageTitle } = usePageConfigContext()
  const viewStageTaskRef = useRef(null)
  const viewProcedureStageRef = useRef(null)

  const {
    show: showDiaryDrawer,
    hide: hideDiaryDrawer,
    DiaryDrawer,
  } = useDiary({
    procedureId: id,
    loading,
  })

  const handleOnDragEnd = (result) => {
    if (!result.destination) return

    const newItems = Array.from(procedure?.procedureStages)
    const [reorderedItem] = newItems.splice(result.source.index, 1)
    newItems.splice(result.destination.index, 0, reorderedItem)

    setProcedure({
      ...procedure,
      procedureStages: newItems,
    })

    return debounceUpdateProcedure({
      ...procedure,
      procedureStages: newItems,
    })
  }

  const debounceUpdateProcedure = debounce(
    useCallback(async ({ id, procedureStages }) => {
      await reorderProcedureStage(id, {
        procedureStageIds: procedureStages.map(({ id }) => id),
      })

      return onRefresh()
    }, []),
    300,
  )

  //fetch procedure
  const fetchProcedure = async () => {
    try {
      setFetching(true)
      const result = await getProcedure(id)

      if (result?.procedure) {
        setProcedure(result.procedure)
      }
    } catch (e) {
      console.log(e)
    } finally {
      setFetching(false)
    }
  }

  useEffect(() => {
    fetchProcedure()
  }, [id])

  useEffect(() => {
    setPageTitle(
      procedure?.name || "Lorem Ipsum has been the industry's standard dummy text ever since",
    )
  }, [procedure])

  const onProcedureSubmit = async (values) => {
    try {
      setLoading(true)
      const result = await updateProcedure(id, values)

      if (result?.updateProcedure) {
        message.success("Cập nhật quy trình thành công !")
        hideProcedureDrawer()
        fetchProcedure()
      }
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }
  const onProcedureRemove = async () => {
    try {
      setLoading(true)
      const result = await removeProcedure(id)

      if (result?.removeProcedure) {
        message.success("Xóa quy trình thành công !")
        history.push("/diaries/procedures")
      }
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }

  const {
    show: showProcedureDrawer,
    hide: hideProcedureDrawer,
    ProcedureDrawer,
  } = useProcedure({
    procedure: {
      ...procedure,
      startDate: procedure?.startDate ? dayjs(procedure?.startDate) : null,
      completedAt: procedure?.completedAt ? dayjs(procedure?.completedAt) : null,
      project: {
        label: procedure?.project?.name,
        value: procedure?.project?.id,
      },
      stages: procedure?.procedureStages?.map(({ stage }) => ({
        label: stage?.name,
        value: stage?.id,
      })),
    },
    loading,
    onSubmit: onProcedureSubmit,
    onRemove: onProcedureRemove,
  })

  const onStageTaskSubmit = async (values) => {
    try {
      const result = await updateStageTask(viewStageTaskRef.current?.id, values)

      if (result.updateStageTask) {
        hideStageTaskDrawer()
        viewStageTaskRef.current = null
        message.success("Cập nhật công việc thành công !")

        onRefresh()
      }
    } catch (e) {
      console.log(e)
    }
  }

  const onProcedureStageSubmit = async (values) => {
    try {
      setUpdatingProcedureStage(true)
      const result = await updateProcedureStage(viewProcedureStageRef.current?.id, values)

      if (result.updateProcedureStage) {
        hideProcedureStageDrawer()
        viewProcedureStageRef.current = null
        message.success("Cập nhật giai đoạn thành công !")

        onRefresh()
      }
    } catch (e) {
      console.log(e)
    } finally {
      setUpdatingProcedureStage(false)
    }
  }

  const {
    show: showProcedureStageDrawer,
    hide: hideProcedureStageDrawer,
    ProcedureStageDrawer,
  } = useProcedureStage({
    width: "800",
    loading: updatingProcedureStage,
    procedureStage: {
      ...viewProcedureStageRef.current,
    },
    onSubmit: onProcedureStageSubmit,
    onClose: () => {
      viewProcedureStageRef.current = null
      hideProcedureStageDrawer()
    },
  })

  const {
    show: showStageTaskDrawer,
    hide: hideStageTaskDrawer,
    StageTaskDrawer,
  } = useStageTask({
    width: "800",
    stageTask: {
      ...viewStageTaskRef.current,
      startedAt: viewStageTaskRef.current?.startedAt
        ? dayjs(viewStageTaskRef.current.startedAt)
        : undefined,
      completedAt: viewStageTaskRef.current?.completedAt
        ? dayjs(viewStageTaskRef.current.completedAt)
        : undefined,
      assignedTo: viewStageTaskRef.current?.assignedTo
        ? {
            label: (
              <span>
                {viewStageTaskRef.current?.assignedTo?.firstName}
                &nbsp;{viewStageTaskRef.current?.assignedTo?.lastName} /&nbsp;
                <UserTag value={viewStageTaskRef.current?.assignedTo?.role} />
              </span>
            ),
            value: viewStageTaskRef.current?.assignedTo?.id,
          }
        : undefined,
      stageTaskSupplies: viewStageTaskRef.current?.stageTaskSupplies?.map((stageTaskSupply) => ({
        ...stageTaskSupply,
        supply: {
          label: stageTaskSupply?.supply?.name,
          value: stageTaskSupply?.supply?.id,
        },
      })),
    },
    onSubmit: onStageTaskSubmit,
    onClose: () => {
      viewStageTaskRef.current = null
      hideStageTaskDrawer()
    },
  })

  if (fetching) {
    return (
      <div className="relative grid h-screen w-full place-items-center">
        <LoadingOutlined className="text-[32px]" />
      </div>
    )
  }

  const onRefresh = () => {
    fetchProcedure()
    setAddStageModalOpen(false)
    setAddTaskModalOpen(false)
    setAssignedToModalOpen(false)
  }

  const onBulkComplete = async () => {
    const promises = selectedStageTasks.map((id) => {
      return updateStageTask(id, { completedAt: dayjs().toISOString() })
    })

    await Promise.all(promises)

    onRefresh()
    setSelectedStageTasks([])
  }

  const onExportData = async () => {
    try {
      const data = await exportProcedure(id)

      const base64 = data.exportProcedure
      const binaryString = window.atob(base64)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "procedure.xlsx"
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e) {
      console.log(e)
    }
  }

  const diffDays = dayjs().diff(dayjs(procedure?.startDate), "day") + 1

  const groupTasksByDate = (stageTasks) => {
    // First sort tasks by date
    const sortedTasks = [...stageTasks].sort((a, b) => dayjs(a.startedAt).diff(dayjs(b.startedAt)))

    // Group tasks by taskId
    const taskGroups = sortedTasks.reduce((acc, task) => {
      const taskId = task.task.id
      if (!acc[taskId]) {
        acc[taskId] = []
      }
      acc[taskId].push(task)
      return acc
    }, {})

    // Convert groups to final format
    const grouped = {}
    Object.values(taskGroups).forEach((tasks) => {
      if (tasks.length > 0) {
        const firstTask = tasks[0]
        const date = dayjs(firstTask.startedAt).format("YYYY-MM-DD")

        if (!grouped[date]) {
          grouped[date] = []
        }

        grouped[date].push({
          ...firstTask,
          continuousTasks: tasks,
        })
      }
    })

    return Object.entries(grouped).sort(([a], [b]) => dayjs(a).diff(dayjs(b)))
  }

  return (
    <div className="max-lg:p-[16px]">
      <div className="flex w-full flex-col items-center justify-between gap-[10px] lg:flex-row">
        <div>
          {procedure?.startDate && (
            <div className="text-[14px] font-medium text-gray-500">
              Ngày bắt đầu: <b>{dayjs(procedure.startDate).format("DD/MM/YYYY")}</b>
            </div>
          )}
        </div>
        <div className="text-end text-[18px] font-medium text-black">
          <Button size="small" className="mr-[6px]" onClick={showDiaryDrawer}>
            <EditOutlined /> Kiểm tra tình trạng
          </Button>
          <Button size="small" className="mr-[6px]" onClick={showProcedureDrawer}>
            <EditOutlined /> Cập nhật thông tin
          </Button>
          <Button size="small" className="" type="primary" onClick={onExportData}>
            <EditOutlined /> Xuất dữ liệu
          </Button>
        </div>
      </div>
      {/* {diffDays > 1 && procedure?.executionDays > 0 && (
        <div className="mt-[10px]">
          <Progress
            percent={
              diffDays >= procedure?.executionDays
                ? 99.9999
                : (diffDays * 100) / procedure?.executionDays
            }
            format={() =>
              diffDays > procedure?.executionDays ? (
                <>Đã hoàn thành</>
              ) : (
                `${diffDays} / ${procedure?.executionDays} ngày`
              )
            }
          />
        </div>
      )} */}
      {/* Add Task or Stage */}
      <div>
        <div className="flex flex-row items-center gap-[16px]">
          <h1 className="text-[24px] font-medium max-lg:my-3">Giai đoạn</h1>
        </div>
        <div>
          <div className="flex w-full flex-col justify-between lg:flex-row">
            <div className="my-[10px] flex flex-row items-center">
              <PlusOutlined className="pr-[6px]" />
              <Button onClick={() => setAddTaskModalOpen(true)} type="link" className="px-2">
                Tạo công việc
              </Button>
              <div className="text-gray-500">hoặc</div>
              <Button onClick={() => setAddStageModalOpen(true)} type="link" className="px-2">
                Tạo giai đoạn mới
              </Button>
            </div>
            {selectedStageTasks.length > 0 && (
              <Popconfirm
                title="Bạn có chắc chắn muốn xác nhận hoàn thành tất cả công việc được chọn không ?"
                onConfirm={onBulkComplete}
              >
                <Button size="sm" type="primary" className="max-lg:mb-[16px] lg:mr-[10px]">
                  Xác nhận hoàn thành
                </Button>
              </Popconfirm>
            )}
          </div>
          <Modal
            title={"Thêm giai đoạn"}
            open={addStageModalOpen}
            onCancel={() => setAddStageModalOpen(false)}
            footer={null}
            width={800}
          >
            <AddStageModal procedure={procedure} onFinish={onRefresh} />
          </Modal>

          <Modal
            centered
            title={"Thêm công việc"}
            open={addTaskModalOpen}
            onCancel={() => setAddTaskModalOpen(false)}
            footer={null}
            width={800}
          >
            <AddTaskModal procedure={procedure} onFinish={onRefresh} />
          </Modal>
        </div>
      </div>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="items">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="flex flex-col gap-[10px]"
            >
              {procedure?.procedureStages?.map((ps, index) => {
                const groupedTasks = groupTasksByDate(ps.stageTasks)

                return (
                  <Draggable key={ps?.id} draggableId={ps?.id + ""} index={index}>
                    {(provided) => (
                      <div
                        {...provided.draggableProps}
                        key={ps?.id}
                        className="group/item flex flex-row gap-[10px] rounded-lg border border-solid border-gray-200 px-2 py-3 shadow lg:px-5"
                        style={{
                          ...provided.draggableProps.style,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          className="hidden"
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                        >
                          <HolderOutlined className="invisible mt-[4px] text-[16px] text-gray-500 group-hover/item:visible" />
                        </div>
                        <div className="w-full">
                          <div className="flex justify-between">
                            <div className="flex items-center gap-[10px] text-[20px] font-medium">
                              <Checkbox
                                onChange={(e) => {
                                  setSelectedStageTasks(
                                    e.target.checked
                                      ? [
                                          ...selectedStageTasks,
                                          ...ps.stageTasks.map(({ id }) => id),
                                        ]
                                      : selectedStageTasks.filter(
                                          (id) => !ps.stageTasks.find((task) => task.id === id),
                                        ),
                                  )
                                }}
                              />
                              {ps?.stage?.name}{" "}
                            </div>
                            <div>
                              <Button
                                onClick={() => {
                                  viewProcedureStageRef.current = {
                                    ...ps,
                                    startDate: ps?.startDate ? dayjs(ps?.startDate) : null,
                                    completedAt: ps?.completedAt && dayjs(ps?.completedAt),
                                  }

                                  showProcedureStageDrawer()
                                }}
                                type="primary"
                              >
                                <EditOutlined />
                              </Button>
                            </div>
                          </div>
                          <Tag className="my-[12px] rounded-xl text-[12px]">
                            <span className="text-[12px] text-gray-500">
                              <span className="max-lg:hidden">Bắt đầu từ:</span>{" "}
                              <b>
                                {ps?.startDate ? dayjs(ps.startDate).format("DD-MM-YYYY") : "-"}
                              </b>
                              {ps?.completedAt && (
                                <span className="ml-[4px]">
                                  <ArrowRightOutlined />{" "}
                                  <span className="max-lg:hidden">Hoàn thành vào:</span>{" "}
                                  <b>{dayjs(ps.completedAt).format("DD-MM-YYYY")}</b>
                                </span>
                              )}
                            </span>
                          </Tag>
                          <div className="ml-[20px] flex flex-col gap-[20px]">
                            {groupedTasks.map(([date, tasks]) => (
                              <div key={date}>
                                {tasks.map((stageTask) => {
                                  const { completedAt, task, assignedTo, continuousTasks } =
                                    stageTask || {}
                                  const isCompleted = !!completedAt
                                  const completedLate =
                                    completedAt && dayjs().isAfter(completedAt, "day")
                                  const isProcessing =
                                    !completedAt && dayjs().isAfter(stageTask?.startedAt)
                                  const isNotStarted =
                                    stageTask?.startedAt && dayjs().isBefore(stageTask?.startedAt)

                                  const isLate =
                                    isProcessing &&
                                    dayjs().isAfter(dayjs(stageTask?.startedAt), "day")

                                  const status = {
                                    [isCompleted]: TASK_STATUS_COLOR.completed,
                                    [completedLate]: TASK_STATUS_COLOR.completed,
                                    [isProcessing]: TASK_STATUS_COLOR.processing,
                                    [isNotStarted]: TASK_STATUS_COLOR.notStarted,
                                    [isLate]: TASK_STATUS_COLOR.late,
                                  }

                                  return (
                                    <div
                                      key={task?.id}
                                      className="cursor-pointer rounded-lg p-3 shadow-lg"
                                    >
                                      <div className="flex items-start gap-[4px] lg:gap-[10px]">
                                        <div onClick={(e) => e.stopPropagation()}>
                                          <Checkbox
                                            className="mt-[1px]"
                                            checked={
                                              Array.isArray(continuousTasks) &&
                                              continuousTasks?.length > 0 &&
                                              (continuousTasks || [])?.every(({ id }) =>
                                                selectedStageTasks?.includes?.(id),
                                              )
                                            }
                                            onChange={(e) => {
                                              setSelectedStageTasks(
                                                e.target.checked
                                                  ? [
                                                      ...selectedStageTasks,
                                                      ...continuousTasks.map(({ id }) => id),
                                                    ]
                                                  : selectedStageTasks.filter(
                                                      (id) =>
                                                        !continuousTasks.find(
                                                          (task) => task.id === id,
                                                        ),
                                                    ),
                                              )
                                            }}
                                          />
                                        </div>
                                        <div className="translate-y-[3px]">
                                          {completedAt ? (
                                            <CheckCircleOutlined
                                              style={{
                                                color: "var(--primary-color)",
                                                fontSize: "17px",
                                              }}
                                            />
                                          ) : (
                                            <StopOutlined style={{ fontSize: "16px" }} />
                                          )}
                                        </div>
                                        <div className="flex w-full flex-col overflow-hidden">
                                          <div
                                            className={`cursor-pointer text-[1.4rem] font-medium hover:font-semibold ${completedAt ? "text-primary-color" : ""}`}
                                            style={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              wordBreak: "break-word",
                                              whiteSpace: "normal",
                                              width: "100%",
                                              maxWidth: "100%",
                                            }}
                                          >
                                            <span>{task?.name}</span>{" "}
                                          </div>

                                          <div className="my-4 flex flex-col gap-1">
                                            <div className="text-xs text-gray-500">
                                              Lịch thực hiện:
                                            </div>
                                            <div className="flex flex-col gap-2">
                                              {(continuousTasks || [])?.map((dateStageTask) => {
                                                const isCompleted = !!dateStageTask?.completedAt

                                                return (
                                                  <div
                                                    key={dateStageTask.startedAt}
                                                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors md:px-5 md:py-3 ${
                                                      isCompleted
                                                        ? "bg-green-500/15"
                                                        : "bg-gray-50/80"
                                                    } hover:bg-gray-100`}
                                                    onClick={(e) => {
                                                      e.stopPropagation()
                                                      viewStageTaskRef.current = dateStageTask
                                                      showStageTaskDrawer()
                                                    }}
                                                  >
                                                    <div
                                                      className="flex max-w-[330px] flex-row items-center gap-2"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                      }}
                                                    >
                                                      <Checkbox
                                                        className="mt-[1px]"
                                                        checked={selectedStageTasks.includes(
                                                          dateStageTask?.id,
                                                        )}
                                                        onChange={(e) => {
                                                          e.stopPropagation()
                                                          setSelectedStageTasks(
                                                            e.target.checked
                                                              ? [
                                                                  ...selectedStageTasks,
                                                                  dateStageTask?.id,
                                                                ]
                                                              : selectedStageTasks.filter(
                                                                  (id) => id !== dateStageTask?.id,
                                                                ),
                                                          )
                                                        }}
                                                      />
                                                      <div
                                                        className={`flex cursor-pointer items-center gap-[6px] rounded-xl !border border-solid px-2 py-1 shadow hover:underline ${
                                                          isCompleted
                                                            ? "border-green-500/30 bg-green-500/15"
                                                            : "border-gray-300"
                                                        }`}
                                                        onClick={(e) => {
                                                          e.stopPropagation()
                                                          viewStageTaskRef.current = dateStageTask
                                                          setAssignedToModalOpen(true)
                                                        }}
                                                      >
                                                        <Avatar
                                                          style={{
                                                            backgroundColor:
                                                              getAvatarBackgroundColor(
                                                                dateStageTask.assignedTo
                                                                  ?.firstName?.[0],
                                                              ),
                                                          }}
                                                          size={"small"}
                                                        >
                                                          {dateStageTask.assignedTo
                                                            ?.firstName?.[0] || ""}
                                                        </Avatar>
                                                        <span className="line-clamp-1 !hidden max-w-[200px] text-ellipsis font-medium md:!inline">
                                                          {dateStageTask.assignedTo?.firstName}{" "}
                                                          {dateStageTask.assignedTo?.lastName}
                                                        </span>
                                                      </div>
                                                      <span className="text-gray-500">
                                                        {dayjs(dateStageTask?.startedAt).format(
                                                          "DD/MM/YYYY",
                                                        )}
                                                      </span>
                                                    </div>
                                                    <div>
                                                      <CheckCircleOutlined
                                                        className={`ml-auto ${isCompleted ? "" : "opacity-0"}`}
                                                        style={{
                                                          color: "var(--primary-color)",
                                                          fontSize: "16px",
                                                        }}
                                                      />
                                                    </div>
                                                    <div className="grow" />
                                                    <Button
                                                      size="small"
                                                      type="link"
                                                      className="ml-2"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        setCommentListOpen(true)
                                                        setCommentModalData(dateStageTask)
                                                      }}
                                                    >
                                                      <CommentOutlined className="max-md:text-[16px]" />
                                                      <span className="ml-1 !hidden md:!inline">
                                                        Thảo luận (
                                                        {dateStageTask?.comments?.length || 0})
                                                      </span>
                                                    </Button>
                                                  </div>
                                                )
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Modal
        centered
        title={"Giao việc"}
        open={assignedToModalOpen}
        onCancel={() => setAssignedToModalOpen(false)}
        footer={null}
        width={800}
      >
        <AssignToModal
          stageTaskId={viewStageTaskRef.current?.id}
          onFinish={() => {
            viewStageTaskRef.current = null
            onRefresh()
          }}
        />
      </Modal>
      <Drawer
        placement="right"
        width={"800"}
        title={"Thảo luận"}
        open={commentListOpen}
        onClose={() => setCommentListOpen(false)}
      >
        <CommentList
          stageTaskId={commentModalData?.id}
          comments={commentModalData?.comments}
          onFinish={() => {
            onRefresh?.()
            setCommentListOpen(false)
          }}
        />
      </Drawer>
      <StageTaskDrawer />
      {/* Add Task or Stage */}
      {/* <StageTab
        procedureStages={procedure?.procedureStages}
        onRequestUpdate={showProcedureDrawer}
        current={currentStage}
        setCurrent={setCurrentStage}
      /> */}
      <ProcedureDrawer />
      <ProcedureStageDrawer />
      <DiaryDrawer />
    </div>
  )
}

export default ProcedurePage
